import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, Download, CheckCircle2, AlertTriangle } from "lucide-react";

export default function AdminCsvImport({ onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (dryRun) => {
    if (!file) return toast.error("Choose a CSV file first");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(`/admin/products/csv-import?dry_run=${dryRun}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(r.data);
      if (!dryRun && r.data.inserted > 0) {
        toast.success(`Imported ${r.data.inserted} product${r.data.inserted === 1 ? "" : "s"}`);
        onDone?.();
      } else if (dryRun) {
        toast.info(`Preview: ${r.data.would_insert} would import · ${r.data.failed} failed`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/admin/products/csv-template", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "cardlobster-products.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download template"); }
  };

  return (
    <div className="border border-white/10 rounded p-6 bg-[var(--cl-surface)] space-y-4" data-testid="csv-import">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-2xl">Bulk CSV Import</h3>
          <p className="text-zinc-400 text-sm mt-1">
            Restock after an auction in one paste. Required columns: <span className="text-[var(--cl-orange)]">title, category, sport, price</span>.
            Image URLs are pipe-delimited (<code className="text-xs">|</code>).
          </p>
        </div>
        <button onClick={downloadTemplate} className="btn-outline text-xs" data-testid="csv-template-btn">
          <Download size={12} className="inline mr-1 -mt-0.5" /> Download template
        </button>
      </div>

      <label className="block border border-dashed border-white/20 rounded p-6 text-center cursor-pointer hover:border-[var(--cl-primary)]"
             data-testid="csv-drop-zone">
        <input type="file" accept=".csv" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} />
        <UploadCloud size={28} className="mx-auto text-zinc-500" />
        <div className="mt-3 text-sm text-zinc-300">
          {file ? file.name : "Click to choose a .csv file (max 5MB)"}
        </div>
      </label>

      <div className="flex gap-3">
        <button onClick={() => submit(true)} disabled={busy || !file} className="btn-outline text-sm" data-testid="csv-dryrun">
          {busy ? "Checking…" : "Preview (Dry Run)"}
        </button>
        <button onClick={() => submit(false)} disabled={busy || !file} className="btn-primary text-sm" data-testid="csv-import-btn">
          {busy ? "Importing…" : "Import Now"}
        </button>
      </div>

      {preview && (
        <div className="border-t border-white/10 pt-4 space-y-2" data-testid="csv-preview">
          <div className="flex gap-6 text-sm">
            <span className="text-emerald-400 flex items-center gap-1">
              <CheckCircle2 size={14}/> {preview.inserted || preview.would_insert || 0} rows OK
            </span>
            {preview.failed > 0 && (
              <span className="text-[var(--cl-primary)] flex items-center gap-1">
                <AlertTriangle size={14}/> {preview.failed} rows failed
              </span>
            )}
          </div>
          <div className="max-h-60 overflow-y-auto space-y-1 mt-3 text-xs font-mono">
            {preview.rows.map((r) => (
              <div key={r.row} className={r.ok ? "text-zinc-400" : "text-[var(--cl-primary)]"}>
                Row {r.row}: {r.ok ? `✓ ${r.title}` : `✗ ${r.error}`}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
